import {
  Box,
  Button,
  Checkbox,
  Grid,
  Group,
  NumberInput,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { IconCheck, IconWindow } from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useCallback, useEffect, useState } from "react";
import { _localization } from "../../../../../config/location";
import { sky_blue } from "../../../../../const/variables";

const ModalItemPrimaryDiscountToAll = ({
  appleDiscountToAll,
  itemType,
  typePromotion,
  dataProps,
}: {
  appleDiscountToAll: any;
  itemType?: string;
  typePromotion: string;
  dataProps?: any[];
}) => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [dataProduct, setDataProduct] = useState<any[]>(dataProps ?? []);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      { accessorKey: "itemPrimaryCode", header: "Mã sản phẩm", size: 125 },
      { accessorKey: "itemPrimaryName", header: "Tên sản phẩm", size: 400 },
      {
        accessorKey: "attribute5",
        header: "Thứ tự hiển thị",
        Cell: ({ row }) => (
          <NumberInput
            placeholder="Thứ tự hiển thị"
            value={row.original.attribute5}
            hideControls
            allowNegative={false}
            onChange={(e) =>
              updateItem(row.original.itemPrimaryCode, String(e))
            }
          />
        ),
      },
    ],
    []
  );

  const entity: {
    discountPercent: number | null;
    discountAmount: number | null;
    quantityMin: number | null;
    quantityMax: number | null;
    quantityApply: number | null;
    attribute1: string | null;
    attribute4: string | null;
    price: number | null;
    attribute5: string | null;
    attribute6: string | null;
  } = {
    discountPercent: null,
    discountAmount: null,
    quantityMin: null,
    quantityMax: null,
    quantityApply: null,
    attribute1: itemType === "primary" ? "0" : null,
    attribute4: null,
    price: null,
    attribute5: null,
    attribute6: null,
  };

  const form = useForm({
    validateInputOnChange: true,
    initialValues: {
      ...entity,
    },
    validate: {
      quantityMax: (value, values) => {
        if (value && values.quantityMin && value < values.quantityMin) {
          return "Số lượng tối đa không được nhỏ hơn số lượng tối thiểu";
        }
      },
      quantityMin: (value, values) => {
        if (value && values.quantityMax && value > values.quantityMax) {
          return "Số lượng tối đa không được nhỏ hơn số lượng tối thiểu";
        }
      },
    },
  });

  const updateItem = useCallback((itemPrimaryCode: string, value: string) => {
    setDataProduct((prevData) =>
      prevData.map((item) =>
        item.itemPrimaryCode === itemPrimaryCode
          ? { ...item, attribute5: value }
          : item
      )
    );
  }, []);

  const table = useMantineReactTable({
    columns,
    data: dataProduct,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    //add custom action buttons to top-left of top toolbar
    enableGlobalFilter: true,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    getRowId: (row) => row.itemCode?.toString(),
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    positionGlobalFilter: "left",
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    // manualPagination: true,
    enablePagination: false, //turn off pagination
    enableRowVirtualization: true, //enable row virtualization
    onRowSelectionChange: setRowSelection,
    rowCount: dataProduct ? dataProduct.length : 0,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "default",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (600 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div>
      <Box component="form">
        {itemType !== "gift" && (
          <Grid mt="md">
            {typePromotion && typePromotion === "1" ? (
              <Grid.Col span={itemType === "primary" ? 4 : 6}>
                <TextInput
                  label={"% chiết khấu"}
                  placeholder={"Nhập %"}
                  type="number"
                  disabled={!!form.values.discountAmount || !!form.values.price}
                  {...form.getInputProps("discountPercent")}
                />
              </Grid.Col>
            ) : (
              <></>
            )}
            <Grid.Col
              span={itemType === "primary" && typePromotion === "1" ? 4 : 6}
            >
              {/* <TextInput
              label={"Số tiền chiết khẩu"}
              placeholder={"Nhập số tiền"}
              type="number"
              disabled={!!form.values.discountPercent}
              {...form.getInputProps("discountAmount")}
            /> */}
              <NumberInput
                label={"Số tiền chiết khấu"}
                placeholder={"Nhập số tiền"}
                hideControls
                thousandSeparator="."
                decimalSeparator=","
                allowNegative={false}
                disabled={
                  typePromotion && typePromotion === "1"
                    ? !!form.values.discountPercent || !!form.values.price
                    : !!form.values.price
                }
                {...form.getInputProps("discountAmount")}
                onChange={(e) => {
                  form.getInputProps("discountAmount").onChange(e);
                }}
              />
            </Grid.Col>
            {itemType === "primary" ? (
              <Grid.Col
                span={itemType === "primary" && typePromotion === "1" ? 4 : 6}
              >
                <NumberInput
                  label="Giá tiền áp dụng trong CTKM"
                  placeholder="Nhập giá tiền"
                  hideControls
                  thousandSeparator="."
                  decimalSeparator=","
                  allowNegative={false}
                  disabled={
                    typePromotion && typePromotion === "1"
                      ? !!form.values.discountAmount ||
                        !!form.values.discountPercent
                      : !!form.values.discountAmount
                  }
                  {...form.getInputProps("price")}
                />
              </Grid.Col>
            ) : (
              <></>
            )}
          </Grid>
        )}

        <Grid>
          <Grid.Col span={itemType !== "addOn" ? 4 : 6}>
            <NumberInput
              label={"Số lượng tối thiểu"}
              placeholder={"Nhập số lượng"}
              hideControls
              {...form.getInputProps("quantityMin")}
            />
          </Grid.Col>
          <Grid.Col span={itemType !== "addOn" ? 4 : 6}>
            <NumberInput
              label={"Số lượng tối đa"}
              placeholder={"Nhập số lượng"}
              hideControls
              {...form.getInputProps("quantityMax")}
            />
          </Grid.Col>
          {itemType !== "primary" ? (
            <Grid.Col span={itemType !== "addOn" ? 4 : 6}>
              <TextInput
                label={"Số lượng áp dụng"}
                placeholder={"Nhập số lượng"}
                type="number"
                {...form.getInputProps("quantityApply")}
              />
            </Grid.Col>
          ) : (
            <Grid.Col span={4}>
              <TextInput
                label={"Số lượng áp dụng"}
                placeholder={"Nhập số lượng"}
                type="number"
                {...form.getInputProps("attribute3")}
              />
            </Grid.Col>
          )}
        </Grid>

        {itemType !== "gift" && (
          <Grid>
            {itemType === "primary" ? (
              <>
                <Grid.Col span={4}>
                  <NumberInput
                    label="Hãng/NCC tài trợ"
                    placeholder="Nhập số tiền"
                    hideControls
                    thousandSeparator="."
                    decimalSeparator=","
                    {...form.getInputProps("attribute6")}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <Checkbox
                    mt={30}
                    label="Nổi bật"
                    checked={form.getValues()?.attribute4 === "Y"}
                    onClick={() =>
                      form.setValues((prev) => ({
                        ...prev,
                        attribute4:
                          form.getValues()?.attribute4 === "Y" ? "N" : "Y",
                      }))
                    }
                  />
                </Grid.Col>
              </>
            ) : (
              <></>
            )}
          </Grid>
        )}

        {itemType === "primary" &&
        (typePromotion === "2" || typePromotion === "4") ? (
          <Box mt={10}>
            <MantineReactTable table={table} />
          </Box>
        ) : (
          <></>
        )}

        {/* {itemType === "gift" ? (
          <Grid>
            <Grid.Col span={12}>
              <Textarea
                label={"Lưu ý thêm"}
                placeholder={"Nhập Lưu ý thêm"}
                autosize
                minRows={4}
                {...form.getInputProps("attribute1")}
              />
            </Grid.Col>
          </Grid>
        ) : (
          <></>
        )} */}

        <Group justify="end" mt="md">
          <Button
            type="button"
            color="gray"
            onClick={() => {
              modals.closeAll();
            }}
            leftSection={<IconWindow size={18} />}
          >
            Đóng
          </Button>
          <Button
            color={sky_blue.base}
            leftSection={<IconCheck size={18} />}
            onClick={() => {
              setTimeout(() => {
                appleDiscountToAll(form.getValues(), dataProduct);
              }, 500);
            }}
          >
            Lưu
          </Button>
          <></>
        </Group>
      </Box>
    </div>
  );
};

export default ModalItemPrimaryDiscountToAll;
